import { useEffect, useRef } from 'react';
import { PiArrowsClockwise, PiPaperPlaneTilt } from 'react-icons/pi';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Loading from 'src/modules/shared/components/atoms/loading';
import { Text } from 'src/modules/shared/components/atoms/text';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useWhatsAppTemplateComposer } from '../../../../hooks/use-whatsapp-template-composer';
import type { WhatsAppTemplateModalContentProps } from '../../../../types/templates/template-modal.types';
import { TemplateComposerHeading } from '../../../molecules/templates/template-composer-heading';
import { TemplateModalContext } from '../../../molecules/templates/template-modal-context';
import { TemplatePicker } from '../../../molecules/templates/template-picker';
import { TemplatePersonalization } from '../../../molecules/templates/template-personalization';
import { TemplatePreview } from '../../../molecules/templates/template-preview';

/**
 * Template library and composer presented inside the shared accessible modal.
 * @param props - Current feature state and consumer-owned callbacks.
 * @returns The corresponding composer surface.
 */
export const WhatsAppTemplateModalContent = (
  props: WhatsAppTemplateModalContentProps
) => {
  const {
    isOpen,
    toNumber,
    fromPhoneNumberId,
    initialPhoneAddress,
    contextLocked,
  } = props;
  const { t } = useI18n();
  const {
    isDesktop,
    mobileView,
    setMobileView,
    selectedToNumber,
    recipient,
    isNumberValid,
    phones,
    phonesLoading,
    phonesError,
    selectedPhoneId,
    selectedPhone,
    handlePhoneChange,
    availabilityLoading,
    availabilityError,
    connectionBlocked,
    templates,
    templatesLoading,
    templatesError,
    refetch,
    selectedTemplate,
    values,
    updateValue,
    draftKey,
    definition,
    pending,
    canSend,
    chooseTemplate,
    close,
    handleSend,
    variants,
    readiness,
    sendError,
  } = useWhatsAppTemplateComposer(props);
  const subtitleKeys = isDesktop
    ? ({
        header: 'whatsappTemplates.subtitle',
        picker: 'whatsappTemplates.chooseHelp',
      } as const)
    : ({
        header: 'whatsappTemplates.subtitleCompact',
        picker: 'whatsappTemplates.chooseHelpCompact',
      } as const);

  const bodyRef = useRef<HTMLFieldSetElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isOpen && sendError?.key === draftKey) {
      errorRef.current?.focus();
    }
  }, [isOpen, sendError, draftKey]);
  const viewKey = JSON.stringify([selectedTemplate?.id, mobileView]);
  const previousViewKey = useRef(viewKey);
  useEffect(() => {
    if (previousViewKey.current === viewKey) return;
    previousViewKey.current = viewKey;
    if (!isOpen) return;
    bodyRef.current?.parentElement?.scrollTo({ top: 0 });
    bodyRef.current?.focus({ preventScroll: true });
  }, [isOpen, viewKey]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      dismissible={!pending}
      ariaLabel={t('whatsappTemplates.title')}
      closeLabel={t('whatsappTemplates.cancel')}
      bodyClassName="!px-4 !py-4"
      footerClassName="px-4 py-3"
      dialogClassName="sm:!max-w-4xl sm:!h-[min(860px,90dvh)] sm:!max-h-[90dvh]"
      header={
        <div className="pr-6">
          <Text as="h2" size="h5" weight="semibold">
            {t('whatsappTemplates.title')}
          </Text>
          <Text size="small" color="secondary" className="mt-1">
            {t(subtitleKeys.header)}
          </Text>
        </div>
      }
      body={
        <fieldset
          ref={bodyRef}
          tabIndex={-1}
          aria-label={t('whatsappTemplates.title')}
          disabled={pending}
          className={`w-full min-w-0 border-0 p-0 outline-none ${selectedTemplate ? 'space-y-5' : 'flex min-h-0 flex-1 flex-col gap-4'}`}
        >
          <TemplateModalContext
            contextLocked={contextLocked}
            toNumber={toNumber}
            selectedToNumber={selectedToNumber}
            isNumberValid={isNumberValid}
            isNumberDebouncedValid={recipient.isDebouncedValid}
            onNumberChange={recipient.handleChange}
            phones={phones}
            phonesLoading={phonesLoading}
            selectedPhone={selectedPhone}
            selectedPhoneId={selectedPhoneId}
            onPhoneChange={handlePhoneChange}
            autoSelectFirst={!fromPhoneNumberId && !initialPhoneAddress}
          />
          {phonesError && (
            <Alert
              variant="warning"
              message={t(
                'communications.messageBox.whatsapp.availabilityError'
              )}
            />
          )}
          {selectedPhone && !availabilityLoading && connectionBlocked && (
            <Alert
              variant="warning"
              role="alert"
              message={t(
                availabilityError
                  ? 'communications.messageBox.whatsapp.availabilityError'
                  : 'communications.messageBox.whatsapp.connectionUnavailable'
              )}
            />
          )}
          {!selectedPhone && !phonesLoading && (
            <Alert
              variant="info"
              message={t('whatsappTemplates.missingSender')}
            />
          )}
          {selectedPhone && !selectedTemplate && (
            <div className="flex shrink-0 items-start justify-between gap-4">
              <div>
                <Text as="h3" weight="medium">
                  {t('whatsappTemplates.choose')}
                </Text>
                <Text size="small" color="secondary">
                  {t(subtitleKeys.picker)}
                </Text>
              </div>
              <Button
                variant="neutral"
                appearance="ghost"
                isIconOnly
                aria-label={t('whatsappTemplates.refresh')}
                title={t('whatsappTemplates.refresh')}
                onClick={() => {
                  void refetch();
                }}
                disabled={templatesLoading}
              >
                <PiArrowsClockwise aria-hidden className="h-5 w-5" />
              </Button>
            </div>
          )}
          {templatesLoading && (
            <div className="flex justify-center py-12">
              <Loading />
            </div>
          )}
          {templatesError && (
            <Alert
              role="alert"
              variant="danger"
              message={t('whatsappTemplates.loadError')}
            />
          )}
          {selectedPhone &&
            !templatesLoading &&
            !templatesError &&
            (selectedTemplate && definition ? (
              <>
                <TemplateComposerHeading
                  template={selectedTemplate}
                  variants={variants}
                  onSelect={chooseTemplate}
                />
                {definition.unavailableReason && (
                  <Alert
                    variant="warning"
                    role="alert"
                    message={t(
                      `whatsappTemplates.unavailable.${definition.unavailableReason}`
                    )}
                  />
                )}
                {!isDesktop && (
                  <div
                    className="grid grid-cols-2 gap-1 rounded-lg bg-secondary-50 p-1"
                    role="group"
                    aria-label={t('whatsappTemplates.preview')}
                  >
                    <Button
                      variant="neutral"
                      appearance={
                        mobileView === 'personalize' ? 'filled' : 'ghost'
                      }
                      aria-pressed={mobileView === 'personalize'}
                      onClick={() => setMobileView('personalize')}
                    >
                      {t('whatsappTemplates.personalize')}
                    </Button>
                    <Button
                      variant="neutral"
                      appearance={mobileView === 'preview' ? 'filled' : 'ghost'}
                      aria-pressed={mobileView === 'preview'}
                      onClick={() => setMobileView('preview')}
                    >
                      {t('whatsappTemplates.preview')}
                    </Button>
                  </div>
                )}
                <div className="grid min-w-0 gap-8 sm:grid-cols-2">
                  {(isDesktop || mobileView === 'personalize') && (
                    <TemplatePersonalization
                      template={selectedTemplate}
                      definition={definition}
                      values={values}
                      disabled={
                        pending || Boolean(definition.unavailableReason)
                      }
                      onChange={updateValue}
                    />
                  )}
                  {(isDesktop || mobileView === 'preview') && (
                    <TemplatePreview
                      template={selectedTemplate}
                      definition={definition}
                      values={values}
                    />
                  )}
                </div>
              </>
            ) : (
              <TemplatePicker
                key={selectedPhone.integrationProviderId}
                templates={templates}
                onSelect={chooseTemplate}
              />
            ))}
        </fieldset>
      }
      footer={
        <div className="flex w-full flex-col gap-3">
          <div className="min-w-0 flex-1" aria-live="polite">
            {sendError?.key === draftKey ? (
              <div
                ref={errorRef}
                tabIndex={-1}
                role="alert"
                className="outline-none"
              >
                <Alert variant="danger" message={sendError.message} />
              </div>
            ) : (
              <Text size="small" weight="medium">
                {readiness}
              </Text>
            )}
            <Text size="tiny" color="secondary">
              {t('whatsappTemplates.replyRequired')}
            </Text>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              variant="neutral"
              appearance="ghost"
              onClick={close}
              disabled={pending}
            >
              {t('whatsappTemplates.cancel')}
            </Button>
            <div className="ml-auto flex gap-2">
              {selectedTemplate && (
                <Button
                  variant="neutral"
                  appearance="outline"
                  onClick={() => {
                    if (!isDesktop && mobileView === 'preview') {
                      setMobileView('personalize');
                    } else {
                      chooseTemplate();
                    }
                  }}
                  disabled={pending}
                >
                  {t('shared.actions.back')}
                </Button>
              )}
              <Button
                variant="neutral"
                className="flex-1"
                loading={pending}
                disabled={!canSend}
                onClick={() => {
                  if (!isDesktop && mobileView === 'personalize') {
                    setMobileView('preview');
                  } else {
                    void handleSend();
                  }
                }}
              >
                <PiPaperPlaneTilt className="h-4 w-4" aria-hidden />
                {t(
                  !isDesktop && mobileView === 'personalize' && selectedTemplate
                    ? 'whatsappTemplates.review'
                    : 'whatsappTemplates.send'
                )}
              </Button>
            </div>
          </div>
        </div>
      }
    />
  );
};
